import { ParsedUrlQuery } from 'querystring';
import camelCase from 'lodash/camelCase';

import {
    RawFilter,
    RawFilterFieldName,
    FiltersState,
    RawFilterValue,
    CheckBoxFilter,
    Filter,
    FiltersSelectedValues
} from '@zoopla/redux-types/filters.types';
import { removeMostFalsyValues } from '@zoopla/object-remove-falsy-values';

const getValue = (value: RawFilterValue, fieldName: RawFilterFieldName) => {
    if (!value) {
        return '';
    }

    switch (fieldName) {
        case 'property-sub-types':
        case 'must-haves':
        case 'incentives':
            return (value as string).split(',');
        default:
            return value;
    }
};

const getDefaultValue = (fieldName: RawFilterFieldName) => {
    switch (fieldName) {
        case 'property-sub-types':
        case 'must-haves':
            return [];
        case 'includeSharedAccommodation':
            return false;
        case 'radius':
            return '0';
        default:
            return '';
    }
};

const getPreOwnedFilter = (
    newHomesSelected: string,
    retirementSelectedInQry: string
): CheckBoxFilter['selectedValue'] =>
    newHomesSelected === 'exclude' ||
    (retirementSelectedInQry === 'false' && newHomesSelected !== 'only');

const getFieldValue = (
    fieldName: RawFilterFieldName,
    value: RawFilterValue,
    query: ParsedUrlQuery
): Filter['selectedValue'] => {
    const {
        include_shared_accommodation,
        buyer_incentive,
        is_retirement_home
    } = query;
    const selectedValue = getValue(value, fieldName);
    const defaulValue = getDefaultValue(fieldName);

    switch (fieldName) {
        case 'auction':
            return !query.is_auction && !selectedValue;
        case 'isSharedOwnership': {
            return query.is_shared_ownership === undefined;
        }
        case 'includeSharedAccommodation': {
            const isSelectedFromBE = selectedValue === 'true';
            const isRemovedByQuery =
                include_shared_accommodation === 'false' ? false : true;

            return isSelectedFromBE || isRemovedByQuery;
        }
        case 'new-homes': {
            return (
                selectedValue === 'only' ||
                (is_retirement_home === 'false' && selectedValue === 'include')
            );
        }
        case 'incentives': {
            return buyer_incentive?.toString() === 'help_to_buy';
        }
        case 'keywords':
            return selectedValue?.toString() || '';
        default: {
            return selectedValue || defaulValue;
        }
    }
};

export const initialiseReduxFilters = (
    fields: RawFilter[],
    query: ParsedUrlQuery
): FiltersSelectedValues => {
    const filtersFromBEFields = fields.reduce(
        (accumulator, current: RawFilter) => {
            const { fieldName, value } = current;

            const camelCaseFieldName = camelCase(fieldName as string);
            const fieldValue = getFieldValue(fieldName, value, query);

            return {
                ...accumulator,
                [camelCaseFieldName]: fieldValue
            };
        },
        {} as FiltersState
    );

    const newHomesField = fields.find(
        (field) => field.fieldName === 'new-homes'
    );
    const { is_retirement_home } = query;

    const result = {
        ...filtersFromBEFields,
        preOwned: getPreOwnedFilter(
            newHomesField?.value as string,
            is_retirement_home as string
        )
    };

    return removeMostFalsyValues(result);
};
